/**
 * A-Z 字母分组联系人列表
 * 支持吸顶效果和键盘导航
 */
import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import CommonItem from "./CommonListItem";
import { IItemData } from "./common.typing";
import { useListKeyboardNavigation } from "./hooks/useListKeyboardNavigation";
import { useLatest } from "./hooks/useLatest";
import locale from "../../_util/locale";

// A-Z 分组数据类型
export interface IGroupedContacts {
    [letter: string]: IItemData[];
}

export interface AlphabetContactListProps {
    /** A-Z 分组的联系人数据 */
    groupedData: any;
    /** 当前已选列表 */
    currentList?: IItemData[];
    /** 设置当前已选 */
    setCurrentList: (list: IItemData[]) => void;
    /** 不可用 id 列表 */
    disabledids?: string[];
    /** 必选列表 */
    requiredList?: IItemData[];
    /** 是否多选 */
    multiple?: boolean;
    /** 是否显示部门信息 */
    isShowOrg?: boolean;
    /** 是否显示全选 */
    showCheckAll?: boolean;
}

const AlphabetContactList: React.FC<AlphabetContactListProps> = ({
    groupedData = {},
    currentList = [],
    setCurrentList,
    disabledids = [],
    requiredList = [],
    isShowOrg = true,
    multiple = true,
    showCheckAll = false,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeLetter, setActiveLetter] = useState<string>("");

    // 排序字母列表（A-Z 在前，# 在最后）
    const sortedLetters = useMemo(() => {
        return Object.keys(groupedData).sort((a, b) => {
            if (a === "#") return 1;
            if (b === "#") return -1;
            return a.localeCompare(b);
        });
    }, [groupedData]);

    // 点击字母锚点，滚动到对应分组
    const handleLetterClick = useCallback((letter: string) => {
        const container = containerRef.current;
        if (!container) return;

        const targetSection = container.querySelector(`[data-letter-section="${letter}"]`) as HTMLElement;
        if (targetSection) {
            // 计算目标分组相对于滚动容器的正确偏移量
            const containerRect = container.getBoundingClientRect();
            const targetRect = targetSection.getBoundingClientRect();
            // 当前滚动位置 + 目标分组相对于容器可视区域顶部的偏移
            const scrollTop = container.scrollTop + (targetRect.top - containerRect.top);
            
            container.scrollTo({
                top: scrollTop,
                behavior: "smooth"
            });
            
            // 更新当前激活的字母
            setActiveLetter(letter);
        }
    }, []);

    // 监听滚动，更新当前激活的字母
    // 注意：依赖项为空数组是因为函数内部只使用了 ref（containerRef）和 setState（setActiveLetter），
    // 这些都是稳定的引用，不会变化。如果未来需要访问其他 props 或 state，需要将相应的值添加到依赖项中。
    const handleScroll = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const containerTop = container.getBoundingClientRect().top;
        let currentLetter = "";

        // 遍历所有分组，找到当前在顶部的分组
        container.querySelectorAll("[data-letter-section]").forEach((section) => {
            const sectionTop = section.getBoundingClientRect().top;
            if (sectionTop <= containerTop + 30) {
                currentLetter = section.getAttribute("data-letter-section") || "";
            }
        });

        if (currentLetter) {
            setActiveLetter(currentLetter);
        }
    }, []); // 依赖项为空是合理的，因为只使用了稳定的 ref 和 setState

    // 绑定滚动事件
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        container.addEventListener("scroll", handleScroll);
        // 初始化时触发一次
        handleScroll();
        return () => container.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);

    // 扁平化列表（用于键盘导航）
    const flatList = useMemo(() => {
        return sortedLetters.flatMap((letter) => groupedData[letter] || []);
    }, [groupedData, sortedLetters]);

    // 使用 useLatest 替代 useEffect 同步 ref 的反模式
    const flatListRef = useLatest(flatList);
    const currentListRef = useLatest(currentList);

    const isDisabled = useCallback((id: string) => {
        const idStr = id.toString();
        return disabledids.includes(idStr) || requiredList.some((r) => r.id.toString() === idStr);
    }, [disabledids, requiredList]);

    // 可选项（未禁用、非必选）用于全选逻辑
    const selectableList = useMemo(() => {
        return flatList.filter((item) => !isDisabled(item.id.toString()));
    }, [flatList, isDisabled]);

    const isAllChecked = useMemo(() => {
        if (selectableList.length === 0) return false;
            const currentIds = new Set(currentList.map((c) => c.id));
            const selectedCount = selectableList.filter((s) =>
                currentIds.has(s.id)
        ).length;
        return selectedCount === selectableList.length;
    }, [selectableList, currentList]);

    const onCheckAll = useCallback(
        (e: CheckboxChangeEvent) => {
            const checked = e.target.checked;
            const selectableIds = new Set(selectableList.map((s) => s.id));
            if (checked) {
                const currentIds = new Set(currentList.map((c) => c.id));
                const toAdd = selectableList.filter((s) => !currentIds.has(s.id));
                setCurrentList([...currentList, ...toAdd]);
            } else {
                setCurrentList(currentList.filter((c) => !selectableIds.has(c.id)));
            }
        },
        [selectableList, currentList, setCurrentList]
    );    

    const handleChange = useCallback((value: string | number, checked: boolean, list: IItemData[]) => {
        const current = currentListRef.current;
        const item = list.find((v) => v.id === value);
        const exists = current.some((v) => v.id === value);

        if (exists && !checked) {
            setCurrentList(current.filter((v) => v.id !== value));
        } else if (!exists && checked && item) {
            setCurrentList([...current, item]);
        }
    }, [setCurrentList]);

    const { highlightedIndex, setHighlightedIndex, handlePointerMove } = useListKeyboardNavigation({
        containerRef,
        listRef: flatListRef,
        dataAttribute: "data-contact-index",
        stickyHeaderOffset: 30,
        onSelectItem: (index, item) => {
            const list = flatListRef.current;
            const isChecked = currentListRef.current.some((v) => v.id === item.id);
            handleChange(item.id, !isChecked, list);
        },
        isItemDisabled: (item) => isDisabled(item.id.toString()),
    });

    useEffect(() => { setHighlightedIndex(-1); }, [flatList.length, setHighlightedIndex]);

    const getGlobalIndex = (letter: string, localIndex: number) => {
        let idx = 0;
        for (const l of sortedLetters) {
            if (l === letter) return idx + localIndex;
            idx += (groupedData[l] || []).length;
        }
        return idx;
    };

    const onChange = (e: CheckboxChangeEvent, item: IItemData, globalIndex: number) => {
        handleChange(item.id, e.target.checked, flatList);
        // 同步高亮索引到点击的项
        setHighlightedIndex(globalIndex);
    };

    return (
        <div className="alphabetContactListWrapper">
            {/* 左侧联系人列表 */}
            <div className="alphabetContactList" ref={containerRef}>
                {showCheckAll && multiple && flatList.length > 0 && (
                    <div className="commonListCheckAll">
                        <Checkbox checked={isAllChecked} onChange={onCheckAll}>
                            <span className="name">{locale("useradd_all_select")}</span>
                        </Checkbox>
                    </div>
                )}
                <Checkbox.Group style={{ width: "100%" }} value={currentList.map((v) => v.id)}>
                    {sortedLetters.map((letter) => {
                        const contacts = groupedData[letter] || [];
                        if (!contacts.length) return null;

                        return (
                            <div key={letter} className="letterSection" data-letter-section={letter}>
                                <div className="letterHeader">{letter}</div>
                                {contacts.map((contact, localIndex) => {
                                    const globalIndex = getGlobalIndex(letter, localIndex);
                                    return (
                                        <div
                                            key={contact.id}
                                            className={`contactItem ${
                                                highlightedIndex === globalIndex ? "highlighted" : "" }`}
                                            data-contact-index={globalIndex}
                                            onPointerMove={() => handlePointerMove(globalIndex)}
                                        >
                                            <Checkbox
                                                value={contact.id}
                                                onChange={(e) => onChange(e, contact, globalIndex)}
                                                disabled={isDisabled(contact.id.toString())}
                                            >
                                                <CommonItem type="member" isShowOrg={isShowOrg} item={contact} />
                                            </Checkbox>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </Checkbox.Group>
            </div>

            {/* 右侧字母锚点导航 - 悬浮定位 */}
            {sortedLetters.length > 0 && (
                <div className="alphabetAnchorNav">
                    {sortedLetters.map((letter) => {
                        const hasContacts = (groupedData[letter] || []).length > 0;
                        if (!hasContacts) return null;
                        return (
                            <div
                                key={letter}
                                className={`anchorItem ${activeLetter === letter ? "active" : ""}`}
                                onClick={() => handleLetterClick(letter)}
                            >
                                {letter}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AlphabetContactList;
